import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import AddProjectWizard from "./AddProjectWizard";
import { Field, TextInput, SelectInput, ErrorBanner } from "../shared/FormElements";

const LIFECYCLE_OPTIONS = ["new", "active", "churned"];

export default function ClientDetail() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  const [form, setForm] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  async function load() {
    setLoading(true);
    setError(null);
    const [clientRes, projectsRes] = await Promise.all([
      supabase.from("clients").select("*").eq("id", clientId).single(),
      supabase
        .from("projects")
        .select("*, project_types(name)")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false }),
    ]);
    if (clientRes.error) setError(clientRes.error.message);
    setClient(clientRes.data);
    setForm(clientRes.data);
    setProjects(projectsRes.data || []);
    setLoading(false);
  }

  function updateForm(patch) {
    setForm((prev) => ({ ...prev, ...patch }));
  }

  async function save() {
    setSaving(true);
    setError(null);
    const { error: updateError } = await supabase
      .from("clients")
      .update({
        company_name: form.company_name,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        industry: form.industry,
        contract_start_date: form.contract_start_date || null,
        contract_renewal_date: form.contract_renewal_date || null,
        monthly_retainer_fee: form.monthly_retainer_fee || null,
        lifecycle_status: form.lifecycle_status,
      })
      .eq("id", clientId);
    setSaving(false);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    setEditing(false);
    load();
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;
  if (!client) return <p className="text-sm text-destructive">Client not found.</p>;

  return (
    <div>
      <div className="text-xs text-muted-foreground mb-3">
        <Link to="/dashboard/agency/clients" className="hover:underline">
          Clients
        </Link>{" "}
        / {client.company_name}
      </div>

      <ErrorBanner error={error} />

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">
            {client.company_name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">{client.company_name}</h1>
            <p className="text-xs text-muted-foreground">
              {client.contact_name} &middot; {client.contact_email}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing((e) => !e)} className="border border-input rounded-lg px-4 py-2 text-sm">
            {editing ? "Cancel" : "Edit client"}
          </button>
          <button
            onClick={() => setShowWizard(true)}
            className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold"
          >
            + Add project
          </button>
        </div>
      </div>

      {editing ? (
        <div className="border border-border rounded-2xl bg-card p-5 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Company name">
              <TextInput value={form.company_name || ""} onChange={(e) => updateForm({ company_name: e.target.value })} />
            </Field>
            <Field label="Industry">
              <TextInput value={form.industry || ""} onChange={(e) => updateForm({ industry: e.target.value })} />
            </Field>
            <Field label="Contact name">
              <TextInput value={form.contact_name || ""} onChange={(e) => updateForm({ contact_name: e.target.value })} />
            </Field>
            <Field label="Contact email">
              <TextInput value={form.contact_email || ""} onChange={(e) => updateForm({ contact_email: e.target.value })} />
            </Field>
            <Field label="Contract start date">
              <TextInput
                type="date"
                value={form.contract_start_date || ""}
                onChange={(e) => updateForm({ contract_start_date: e.target.value })}
              />
            </Field>
            <Field label="Contract renewal date">
              <TextInput
                type="date"
                value={form.contract_renewal_date || ""}
                onChange={(e) => updateForm({ contract_renewal_date: e.target.value })}
              />
            </Field>
            <Field label="Monthly retainer fee">
              <TextInput
                type="number"
                value={form.monthly_retainer_fee || ""}
                onChange={(e) => updateForm({ monthly_retainer_fee: e.target.value })}
              />
            </Field>
            <Field label="Lifecycle status">
              <SelectInput value={form.lifecycle_status} onChange={(e) => updateForm({ lifecycle_status: e.target.value })}>
                {LIFECYCLE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </SelectInput>
            </Field>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={save}
              disabled={saving}
              className="bg-primary text-primary-foreground rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <InfoCard label="Monthly retainer" value={client.monthly_retainer_fee ? `₹${client.monthly_retainer_fee}` : "Not set"} />
          <InfoCard label="Contract renewal" value={client.contract_renewal_date || "Not set"} />
          <InfoCard label="Lifecycle status" value={client.lifecycle_status} />
          <InfoCard label="Industry" value={client.industry || "Not set"} />
        </div>
      )}

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <span className="font-semibold text-sm text-foreground">Projects</span>
        </div>
        {projects.length === 0 ? (
          <p className="text-sm text-muted-foreground p-5">No projects yet for this client.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-accent/60">
                  <td className="px-5 py-3.5 font-medium text-foreground">
                    <Link to={`/dashboard/agency/projects/${p.id}`} className="hover:underline">
                      {p.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <span className="font-mono text-[10.5px] bg-accent text-foreground rounded-full px-2.5 py-1">
                      {p.project_types?.name || "Custom"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showWizard && (
        <AddProjectWizard
          presetClientId={clientId}
          onClose={() => setShowWizard(false)}
          onSaved={() => {
            setShowWizard(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="border border-border rounded-2xl bg-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{label}</div>
      <div className="text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}
